import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Mail, CheckCircle2, XCircle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

export default function MailPage() {
  const { authAxios } = useAuth();
  const [mail, setMail] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/mail/latest');
      setMail(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Mail fetch error:', err);
      toast.error('Failed to fetch mail health');
      setMail([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Mail className="w-8 h-8 text-[#EF694C] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
          Mail Health
        </h1>
        <p className="text-sm text-[#a1a1aa] mt-1">
          {mail.length} mail domains monitored
        </p>
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa]">Domain</TableHead>
                <TableHead className="text-[#a1a1aa]">MX Records</TableHead>
                <TableHead className="text-[#a1a1aa]">SMTP Check</TableHead>
                <TableHead className="text-[#a1a1aa]">SPF</TableHead>
                <TableHead className="text-[#a1a1aa]">DMARC</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mail.map((item) => (
                <TableRow key={item.domain} className="border-white/5 hover:bg-white/[0.03]">
                  <TableCell className="font-medium text-white">
                    {item.domain}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs text-[#a1a1aa]">
                      {item.data?.mx_records?.length > 0 ? (
                        item.data.mx_records.map((mx, i) => <span key={i}>{mx}</span>)
                      ) : (
                        <span className="text-[#52525b] italic">No MX records</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.data?.smtp_ok ? (
                      <Badge 
                        className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 cursor-help"
                        title={item.data.smtp_banner || "SMTP Connection Verified"}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> OK
                      </Badge>
                    ) : item.data?.spf_record || item.data?.dmarc_record ? (
                      <Badge 
                        className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 cursor-help"
                        title="Soft pass: Port 25 blocked but DNS valid"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> OK
                      </Badge>
                    ) : (
                      <Badge 
                        className="bg-red-500/10 text-red-500 border-red-500/20 cursor-help"
                        title={item.data?.error || "Connection Failed"}
                      >
                        <XCircle className="w-3 h-3 mr-1" /> Fail
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.data?.spf_record ? (
                      <div className="flex items-center gap-1 text-emerald-500 text-xs">
                        <ShieldCheck className="w-3 h-3" />
                        <span className="truncate max-w-[150px]" title={item.data.spf_record}>Present</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-red-500 text-xs">
                        <ShieldAlert className="w-3 h-3" />
                        <span>Missing</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.data?.dmarc_record ? (
                      <div className="flex items-center gap-1 text-emerald-500 text-xs">
                        <ShieldCheck className="w-3 h-3" />
                        <span className="truncate max-w-[150px]" title={item.data.dmarc_record}>Present</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-amber-500 text-xs">
                        <ShieldAlert className="w-3 h-3" />
                        <span>Missing</span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {mail.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#52525b]">
                    No mail domains checked yet. Run a domain scan.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
